import { useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { CalendarIcon, CheckIcon, CheckCircleIcon, ChatBubbleBottomCenterTextIcon, EnvelopeIcon, ArrowPathIcon, ChartBarIcon } from '@heroicons/react/24/outline';

const FreeConsultancyPlan = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isBooked, setIsBooked] = useState(false); // Show form by default
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    topic: '',
    details: '',
    consent: false,
  });

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  try {
    const form = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      form.append(key, typeof value === "boolean" ? (value ? "1" : "0") : value);
    });

    const response = await fetch("https://vorstel.com/Consultation.php", {
      method: "POST",
      body: form,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data || typeof data.success === "undefined") {
      throw new Error("Invalid response format");
    }

    if (data.success) {
      toast.success(data.message || "Success!");
      // Clear the form
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        topic: '',
        details: '',
        consent: false,
      });
      setIsBooked(false);
    } else {
      toast.error(data.message || "Operation failed");
    }

  } catch (error) {
    console.error("Submission error:", error);
    toast.error(error.message || "Network error. Please try again.");
  }
};


  return (
    <div className="min-h-[300px] bg-gradient-to-br from-blue-50 to-indigo-50 py-20 px-6 sm:px-6 lg:px-8">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Zero-Fee  <span className="text-blue-600">Solution Evaluation</span></h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Free Expert advice on Automation, IT Strategy, Cloud Solutions, and Software Development
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'overview' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              Service Overview
            </button>
            <button
              onClick={() => setActiveTab('process')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'process' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              Our Process
            </button>
            <button
              onClick={() => setActiveTab('promotion')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'promotion' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              How it Works
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {activeTab === 'overview' && (
            <div className="p-6 md:p-8 max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Service Overview</h2>
                <p className="text-gray-500 max-w-2xl mx-auto">Expert consulting services to transform your business operations</p>
              </div>

              <div className="grid md:grid-cols-2 gap-10">
                {/* Topics Card */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-5">
                    <h3 className="text-xl font-semibold text-gray-800">Our Expertise</h3>
                  </div>
                  <div className="p-6">
                    <ul className="space-y-4">
                      {[
                        "Business Process Automation",
                        "IT Strategy & Digital Transformation",
                        "Cloud Solutions & Migration",
                        "Custom Software Development"
                      ].map((topic, index) => (
                        <li key={index} className="flex items-start">
                          <div className="flex-shrink-0 mt-0.5">
                            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-indigo-100 text-indigo-600">
                              <CheckCircleIcon className="h-4 w-4" />
                            </div>
                          </div>
                          <span className="ml-3 text-gray-700 font-medium">{topic}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Delivery Methods Card */}
                <div className="space-y-6">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-5">
                      <h3 className="text-xl font-semibold text-gray-800">How We Work</h3>
                    </div>
                    <div className="p-6 space-y-5">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-indigo-100 text-indigo-600">
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                          </div>
                        </div>
                        <div className="ml-4">
                          <h4 className="font-semibold text-gray-900">Start With Free Consultation</h4>
                          <p className="mt-1 text-gray-600 text-sm">The virtual meeting will be held via Microsoft Teams, Zoom, or Google Meet</p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-purple-100 text-purple-600">
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                        </div>
                        <div className="ml-4">
                          <h4 className="font-semibold text-gray-900">Follow-Up Sessions (Service fee applies)</h4>
                          <p className="mt-1 text-gray-600 text-sm">In-person meetings for strategic partnerships are available</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {!isBooked && (
                    <button
                      onClick={() => setIsBooked(true)}
                      className="w-full py-3 px-6 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-medium rounded-lg shadow-md transition-all duration-300 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    >
                      Book Free Consultation
                      <span className="ml-2">→</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'process' && (
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Our Structured Process</h2>

              <div className="space-y-8">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="md:w-1/3 bg-indigo-50 p-6 rounded-lg">
                    <div className="flex items-center mb-3">
                      <div className="bg-indigo-100 p-2 rounded-full mr-3">
                        <ChatBubbleBottomCenterTextIcon className="h-6 w-6 text-indigo-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800">Pre-Consultation</h3>
                    </div>
                    <p className="text-gray-600">We gather essential information to make the most of our time together.</p>
                    <ul className="mt-4 space-y-2">
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-5 w-5 text-indigo-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">Short questionnaire about your needs</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-5 w-5 text-indigo-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">Information about your budget and expectations</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-5 w-5 text-indigo-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">Flexible scheduling based on your availability</span>
                      </li>
                    </ul>
                  </div>

                  <div className="md:w-1/3 bg-blue-50 p-6 rounded-lg">
                    <div className="flex items-center mb-3">
                      <div className="bg-blue-100 p-2 rounded-full mr-3">
                        <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800">Consultation Session</h3>
                    </div>
                    <p className="text-gray-600">A focused discussion to understand your challenges and opportunities.</p>
                    <ul className="mt-4 space-y-2">
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">Deep dive into your specific needs</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">High-value strategic insights</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">Potential solution frameworks</span>
                      </li>
                    </ul>
                  </div>

                  <div className="md:w-1/3 bg-purple-50 p-6 rounded-lg">
                    <div className="flex items-center mb-3">
                      <div className="bg-purple-100 p-2 rounded-full mr-3">
                        <EnvelopeIcon className="h-6 w-6 text-purple-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800">Follow-Up</h3>
                    </div>
                    <p className="text-gray-600">We provide clear next steps based on our discussion.</p>
                    <ul className="mt-4 space-y-2">
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-5 w-5 text-purple-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">Summary email with key takeaways</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-5 w-5 text-purple-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">Customized proposal (if applicable)</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircleIcon className="h-5 w-5 text-purple-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">Exclusive offers for continued engagement</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">What You Can Expect</h3>
                  <p className="text-gray-600 mb-4">
                    Our free consultations focus on strategic advice and high-level solutions. We'll help you understand your options and potential paths forward without giving away detailed execution plans.
                  </p>
                  <div className="flex items-center text-sm text-gray-500">
                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span>Note: Implementation requires a separate engagement</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* {activeTab === 'promotion' && (
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Step-by-Step: Our Automation Evaluation Method</h2>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Our Promotion Channels</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <div className="bg-green-100 p-2 rounded-full mr-3">
                        <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Dedicated Website</h4>
                        <p className="text-sm text-gray-600">Landing page for booking free consultations</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-blue-100 p-2 rounded-full mr-3">
                        <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Social Media</h4>
                        <p className="text-sm text-gray-600">Success stories and case studies</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-indigo-100 p-2 rounded-full mr-3">
                        <svg className="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">LinkedIn & Forums</h4>
                        <p className="text-sm text-gray-600">Engaging in discussions and offering expert advice</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-purple-100 p-2 rounded-full mr-3">
                        <EnvelopeIcon className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Email Campaigns</h4>
                        <p className="text-sm text-gray-600">Direct outreach with free consultation offers</p>
                      </div>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Performance Tracking</h3>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <div className="flex items-center mb-4">
                      <div className="bg-gray-100 p-2 rounded-full mr-3">
                        <ChartBarIcon className="h-5 w-5 text-gray-600" />
                      </div>
                      <h4 className="font-medium text-gray-900">Metrics We Monitor</h4>
                    </div>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <ArrowPathIcon className="h-5 w-5 text-gray-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">Booking-to-conversion rates</span>
                      </li>
                      <li className="flex items-start">
                        <ArrowPathIcon className="h-5 w-5 text-gray-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">Client feedback and satisfaction</span>
                      </li>
                      <li className="flex items-start">
                        <ArrowPathIcon className="h-5 w-5 text-gray-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">Effectiveness of different channels</span>
                      </li>
                    </ul>
                    <p className="mt-4 text-sm text-gray-600">
                      We continuously optimize our approach based on data to deliver the best value to our clients.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-indigo-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Ready to Get Started?</h3>
                <p className="text-gray-600 mb-4">
                  Book your free consultation today and discover how we can help transform your business with strategic IT solutions.
                </p>
                <button
                  onClick={() => setIsBooked(true)}
                  className="px-6 py-2 bg-indigo-600 text-white font-medium rounded-lg shadow hover:bg-indigo-700 transition-colors"
                >
                  Schedule Your Free Session
                </button>
              </div>
            </div>
          )} */}

          {activeTab === 'promotion' && (
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Our Evaluation Process</h2>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">What we will do for you</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <div className="bg-green-100 p-2 rounded-full mr-3">
                        <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Initial Discovery Call (15-30 mins)</h4>
                        <p className="text-sm text-gray-600">Understanding client needs, goals, and pain points</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-blue-100 p-2 rounded-full mr-3">
                        <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Assessment & Recommendation</h4>
                        <p className="text-sm text-gray-600">Analysing the systems/processes and delivering an actionable report</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-indigo-100 p-2 rounded-full mr-3">
                        <svg className="h-5 w-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Futuristic Approaches</h4>
                        <p className="text-sm text-gray-600">MVP development and long-term partnership planning</p>
                      </div>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Focus Areas</h3>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <div className="flex items-center mb-4">
                      <div className="bg-gray-100 p-2 rounded-full mr-3">
                        <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                      <h4 className="font-medium text-gray-900">Our Expertise</h4>
                    </div>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <CheckIcon className="h-5 w-5 text-gray-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">IT Infrastructure</span>
                      </li>
                      <li className="flex items-start">
                        <CheckIcon className="h-5 w-5 text-gray-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">Cloud Assessment</span>
                      </li>
                      <li className="flex items-start">
                        <CheckIcon className="h-5 w-5 text-gray-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">Process Automation Gap Analysis</span>
                      </li>
                      <li className="flex items-start">
                        <CheckIcon className="h-5 w-5 text-gray-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">Software/Product Feasibility Study</span>
                      </li>
                      <li className="flex items-start">
                        <CheckIcon className="h-5 w-5 text-gray-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">Digital Transformation Readiness</span>
                      </li>
                      <li className="flex items-start">
                        <CheckIcon className="h-5 w-5 text-gray-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">UX/UI Feedback and Suggestions</span>
                      </li>
                      <li className="flex items-start">
                        <CheckIcon className="h-5 w-5 text-gray-500 mt-0.5 mr-2" />
                        <span className="text-gray-700">Tech Stack Recommendations</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-indigo-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Ready to Transform Your Business?</h3>
                <p className="text-gray-600 mb-4">
                  Schedule your free discovery call today and let's explore how we can optimize your IT systems and processes.
                </p>
                <button
                  onClick={() => setIsBooked(true)}
                  className="px-6 py-2 bg-indigo-600 text-white font-medium rounded-lg shadow hover:bg-indigo-700 transition-colors"
                >
                  Book Your Free Consultation
                </button>
              </div>
            </div>
          )}

          {/* Booking Form */}
          {isBooked && (
            <div className="p-8">
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <div className="px-8 py-6 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-gray-900">Book Your Free Consultation</h2>
                  <p className="text-gray-600 mt-1">Fill out this form to schedule your session</p>
                </div>

                <div className="p-8">
                  <form onSubmit={handleSubmit} enctype="multipart/form-data"
                    className="space-y-6">
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                          Full Name
                        </label>
                        <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="John Doe" />
                      </div>
                      <div>
                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                          Company
                        </label>
                        <input type="text" id="company" name="company" value={formData.company} onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Your Company" />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="you@example.com" />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                          Phone
                        </label>
                        <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="+1 (555) 123-4567" />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="topic" className="block text-sm font-medium text-gray-700 mb-1">
                        Consultation Topic
                      </label>
                      <select id="topic" name="topic" value={formData.topic} onChange={handleInputChange} required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500">
                        <option value="">Select a topic</option>
                        <option value="automation">Digital & IT Solutions</option>
                        <option value="strategy">SAP Transformation & Migration</option>
                        <option value="cloud">GenAI & ML</option>
                        {/* <option value="software">Custom Software Development</option> */}
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="details" className="block text-sm font-medium text-gray-700 mb-1">
                        Your Challenge/Needs
                      </label>
                      <textarea id="details" name="details" value={formData.details} onChange={handleInputChange} required rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Briefly describe your current challenge or what you hope to achieve..."></textarea>
                    </div>
                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input id="consent" name="consent" type="checkbox" checked={formData.consent} onChange={handleInputChange}
                          className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="consent" className="font-medium text-gray-700">
                          I agree to receive follow-up communications
                        </label>
                        <p className="text-gray-500">
                          We'll send a confirmation and may follow up with additional information.
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button type="button" onClick={() => setIsBooked(false)}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white
            hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Cancel
                      </button>
                      <button type="submit"
                        className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        Book Consultation
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FreeConsultancyPlan;